import type { ImageProps } from 'next/image';
import { useIntl } from 'react-intl';

import { NextImageWithLoader } from '@/components/NextImageWithLoader';
import type { Maybe } from '@/lib/types';
import { pdpMessages } from '@/messages/product';

import * as S from './Feature.styles';

export interface FeatureProps {
  className?: string;
  displayName?: string;
  handleModalOpen?: (data: string) => void;
  icon: Pick<ImageProps, 'src' | 'alt'>;
  name: string;
  value: Maybe<string>;
}

export const Feature = ({
  className,
  handleModalOpen,
  icon,
  name,
  value,
  displayName,
}: FeatureProps) => {
  const { formatMessage } = useIntl();

  const formatValue = value ? value.replace(/(\.0+)(?!\d)/, '') : value;

  return (
    <S.Wrapper className={className} data-test-id='FeatureWrapper'>
      {name !== formatMessage(pdpMessages.color) && (
        <NextImageWithLoader
          alt={icon.alt}
          data-test-id='FeatureNextImage'
          height={70}
          loaderHeight={70}
          loaderWidth={70}
          loading='eager'
          src={icon.src ?? ''}
          width={70}
        />
      )}
      {name === formatMessage(pdpMessages.color) && (
        <S.ColorFeature>
          <S.ColorCircle $color={value}></S.ColorCircle>
        </S.ColorFeature>
      )}
      <S.Name data-test-id='FeatureName'>{name}</S.Name>
      <S.ValueBox data-test-id='FeatureValueBox'>
        <S.Value data-test-id='FeatureValue'>
          {displayName ?? formatValue}
        </S.Value>
        {name === formatMessage(pdpMessages.keyFeaturesWaterResistance) &&
          value && (
            <S.InfoBadgeIcon
              data-test-id='FeatureInfoBadgeIcon'
              onClick={() => handleModalOpen?.(value)}
            />
          )}
      </S.ValueBox>
    </S.Wrapper>
  );
};

Feature.displayName = 'Feature';
